/* Killa Tarot — Shopify storefront app
 * Auth: daily HMAC signed by Shopify Liquid, verified by api.php on every request.
 * No tokens stored — auth fields (customer_id, email, date, hmac) are sent with each call.
 */

const TarotApp = (() => {

  const cfg = window.TAROT_CONFIG || {};
  const API = (cfg.apiBase || '').replace(/\/$/, '') + '/api.php';

  const auth = {
    customer_id: cfg.customerId,
    email:       cfg.email,
    name:        cfg.name,
    date:        cfg.date,
    hmac:        cfg.hmac,
  };

  let state = {
    user:               null,
    category:           null,
    subcategory:        null,
    question:           null,
    spreadCount:        1,
    currentReading:     null,
    followupPoolIndex:  0,
  };

  // ─── Questions & follow-up pools ─────────────────────────────────────────
  const QUESTIONS = {
    love: {
      title: '愛情感情',
      subcats: [
        { id: 'single',   title: '單身求緣', img: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=400&q=80',
          questions: ['我最近有機會遇到合適的對象嗎？','什麼樣的人適合我？','我在感情上有什麼阻礙需要克服？','我現在的狀態適合談戀愛嗎？','如何提升自己的桃花運？','我對感情的態度是否需要調整？','我最近會遇到哪種類型的人？','宇宙給我在感情上的提示是什麼？'] },
        { id: 'dating',   title: '戀愛中',   img: 'https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?w=400&q=80',
          questions: ['我和伴侶的感情現在處於什麼狀態？','我們之間有什麼需要改善的地方？','伴侶對這段感情的看法如何？','我們的未來走向如何？','如何讓我們的感情更穩固？','我的伴侶是否真心愛我？','近期我們的關係會有什麼變化？'] },
        { id: 'trouble',  title: '感情困擾', img: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400&q=80',
          questions: ['我們的感情值得繼續嗎？','如何處理感情中的矛盾？','分手後我該如何面對？','對方還有感情嗎？','我是否應該主動挽回關係？','如何走出失戀的陰影？'] },
        { id: 'marriage', title: '婚姻家庭', img: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?w=400&q=80',
          questions: ['我們的婚姻關係健康嗎？','如何改善與配偶的關係？','我們合適結婚嗎？','婚後生活會如何？','如何重燃婚姻中的熱情？','如何平衡家庭與個人生活？'] },
      ],
    },
    career: {
      title: '事業財運',
      subcats: [
        { id: 'work',     title: '工作環境', img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&q=80',
          questions: ['我目前的工作環境適合我嗎？','我與上司/同事的關係如何？','我在職場上有什麼機遇？','近期工作壓力如何緩解？','我的工作穩定性如何？','工作中我需要注意什麼？'] },
        { id: 'change',   title: '轉職發展', img: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&q=80',
          questions: ['我現在適合轉換工作嗎？','新的工作機會對我來說好嗎？','我應該往哪個方向發展？','轉職後我的生活會有什麼變化？','轉職的最佳時機是什麼時候？'] },
        { id: 'business', title: '創業投資', img: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&q=80',
          questions: ['我適合創業嗎？','我的創業計劃有成功的可能嗎？','現在是投資的好時機嗎？','哪個投資方向對我較有利？','如何提升我的財運？'] },
        { id: 'growth',   title: '職業發展', img: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&q=80',
          questions: ['我在職業上的下一步應該是什麼？','我有晉升的機會嗎？','我的長遠職業目標能實現嗎？','如何建立更好的職業人脈？','近期會有重要的職業機遇嗎？'] },
      ],
    },
  };

  const FOLLOWUP_POOLS = {
    love: [
      ['我應該主動表達心意嗎？','如何讓對方更了解我？','這段關係值得我投入時間嗎？'],
      ['我需要改變哪些行為才能改善感情？','我的期望是否太高了？','如何重建信任？'],
      ['分開一段時間對感情有幫助嗎？','我是否在這段關係中失去了自我？','怎樣才能讓雙方都感到被重視？'],
      ['我需要接受輔導或諮詢嗎？','與其等待對方改變，我能做什麼？','如何在關係中保持清醒的頭腦？'],
    ],
    career: [
      ['我現在應該接受這個機會嗎？','是什麼阻礙了我的進步？','我需要提升哪方面的技能？'],
      ['如何與職場中重要的人建立更好的關係？','我的薪酬與付出是否相符？','有什麼我沒有考慮到的職業選擇？'],
      ['如果失敗了，我有後備計劃嗎？','我對工作的熱情還在嗎？','短期與長期目標如何平衡？'],
      ['我需要尋找導師或職業顧問嗎？','我的職業選擇是否符合我的價值觀？','現在的付出在三年後會有回報嗎？'],
    ],
  };

  // ─── API helper — auth fields merged into every POST body ────────────────
  async function api(action, body = {}, method = 'POST') {
    const url     = `${API}?action=${action}`;
    const payload = method === 'POST' ? { ...auth, ...body } : null;
    const res     = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: payload ? JSON.stringify(payload) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'API Error');
    return data;
  }

  // ─── Screen management ───────────────────────────────────────────────────
  function show(id) {
    document.querySelectorAll('.screen, #screen-auth-gate, #screen-loading').forEach(el => el.classList.add('hidden'));
    document.getElementById(id)?.classList.remove('hidden');
  }

  function goTo(id) { show(id); }

  function updateCreditsDisplay() {
    const c = state.user?.credits ?? '…';
    document.getElementById('credits-display').textContent = c;
    const el = document.getElementById('credits-store-display');
    if (el) el.textContent = c;
  }

  // ─── Init / Auth ─────────────────────────────────────────────────────────
  async function init() {
    if (!cfg.loggedIn) {
      show('screen-auth-gate');
      return;
    }
    show('screen-loading');
    try {
      const data = await api('auth');
      state.user = data.user;
      document.getElementById('user-name-display').textContent = state.user.name;
      updateCreditsDisplay();
      document.getElementById('app-shell').classList.remove('hidden');
      show('screen-home');
    } catch {
      show('screen-auth-gate');
    }
  }

  // ─── Category / Subcategory / Question ───────────────────────────────────
  function selectCategory(cat) {
    state.category = cat;
    const catData  = QUESTIONS[cat];
    document.getElementById('subcat-title').textContent = catData.title;
    document.getElementById('subcat-grid').innerHTML = catData.subcats.map(sc => `
      <button onclick="TarotApp.selectSubcat('${sc.id}')"
        class="relative overflow-hidden h-36 flex flex-col items-end justify-end p-3 transition-all active:scale-95"
        style="border-radius:16px; border:1px solid rgba(200,168,75,0.3);">
        <img src="${sc.img}" class="absolute inset-0 w-full h-full object-cover" style="opacity:0.55;" alt="">
        <div class="absolute inset-0" style="background:linear-gradient(to top, rgba(20,14,6,0.7) 30%, transparent 100%);border-radius:16px;"></div>
        <span class="relative font-serif font-semibold text-sm" style="color:#F5EDD6;text-shadow:0 1px 4px rgba(0,0,0,0.5);">${sc.title}</span>
      </button>`).join('');
    show('screen-subcategory');
  }

  function selectSubcat(id) {
    state.subcategory = id;
    const subcat = QUESTIONS[state.category].subcats.find(s => s.id === id);
    document.getElementById('question-list').innerHTML = subcat.questions.map(q => `
      <button onclick="TarotApp.pickQuestion(this)" data-q="${q.replace(/"/g,'&quot;')}"
        class="w-full text-left px-4 py-3 rounded-xl text-sm transition-all"
        style="background:#FFFFFF; border:1px solid rgba(180,150,70,0.25); color:#2C2010;">
        ${q}
      </button>`).join('');
    document.getElementById('custom-question').value = '';
    document.getElementById('q-char-count').textContent = '0';
    state.question = null;
    show('screen-question');
  }

  function pickQuestion(btn) {
    document.querySelectorAll('#question-list button').forEach(b => {
      b.style.borderColor = 'rgba(180,150,70,0.2)';
      b.style.background  = '#FFFFFF';
    });
    btn.style.borderColor = '#D4AF37';
    btn.style.background  = 'rgba(212,175,55,0.08)';
    document.getElementById('custom-question').value = '';
    state.question = btn.dataset.q;
  }

  function confirmQuestion() {
    const custom = document.getElementById('custom-question').value.trim();
    if (custom) state.question = custom;
    if (!state.question) { alert('請選擇或輸入問題'); return; }
    state.spreadCount = null;
    document.getElementById('spread-confirm-btn').classList.add('hidden');
    const b1 = document.getElementById('spread-btn-1');
    const b3 = document.getElementById('spread-btn-3');
    if (b1) { b1.style.outline = 'none'; b1.style.opacity = '1'; }
    if (b3) { b3.style.outline = 'none'; b3.style.opacity = '1'; }
    show('screen-spread');
  }

  // ─── Spread + Reading ────────────────────────────────────────────────────
  function selectSpread(count) {
    state.spreadCount = count;

    const btn1 = document.getElementById('spread-btn-1');
    const btn3 = document.getElementById('spread-btn-3');

    btn1.style.outline = 'none';
    btn3.style.outline = 'none';
    btn1.style.opacity  = count === 1 ? '1' : '0.5';
    btn3.style.opacity  = count === 3 ? '1' : '0.5';

    const selected = count === 1 ? btn1 : btn3;
    selected.style.outline       = '2px solid #D4AF37';
    selected.style.outlineOffset = '2px';
    selected.style.opacity       = '1';

    document.getElementById('spread-confirm-btn').classList.remove('hidden');
  }

  function confirmSpread() {
    if (!state.spreadCount) return;
    document.getElementById('spread-btn-1').style.outline = 'none';
    document.getElementById('spread-btn-3').style.outline = 'none';
    document.getElementById('spread-btn-1').style.opacity = '1';
    document.getElementById('spread-btn-3').style.opacity = '1';
    document.getElementById('spread-confirm-btn').classList.add('hidden');
    const stopShuffle = startShuffle();
    fetchReading(stopShuffle);
  }

  function startShuffle() {
    show('screen-shuffle');
    const cards = Array.from(document.querySelectorAll('.shuffle-card'));
    let active = true;

    cards.forEach(c => {
      c.style.transition = '';
      c.style.transform  = '';
      c.style.opacity    = '1';
      c.style.boxShadow  = '';
    });

    function cycle() {
      if (!active) return;
      // fan out with slight vertical offset per card for a mixing look
      cards.forEach((c, i) => {
        const xOff = (i - 2) * 20;
        const rot  = (i - 2) * 7;
        const yOff = Math.abs(i - 2) * 3;
        c.style.transition = `transform 0.38s ease ${i * 0.045}s`;
        c.style.transform  = `translateX(${xOff}px) translateY(${yOff}px) rotate(${rot}deg)`;
      });
      setTimeout(() => {
        if (!active) return;
        cards.forEach(c => {
          c.style.transition = 'transform 0.28s ease';
          c.style.transform  = '';
        });
        setTimeout(cycle, 360);
      }, 500);
    }

    setTimeout(cycle, 80);

    // returns a Promise that stops the loop, plays the selection, then resolves
    return function(n) {
      active = false;
      return new Promise(resolve => {
        setTimeout(() => {
          // snap all cards to center cleanly first
          cards.forEach(c => {
            c.style.transition = 'transform 0.18s ease, opacity 0.18s ease';
            c.style.transform  = '';
            c.style.opacity    = '1';
          });
          setTimeout(() => {
            // pick n random indices
            const pool  = [0,1,2,3,4];
            const picks = [];
            while (picks.length < n) {
              picks.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
            }
            picks.sort((a,b) => a - b);
            cards.forEach((c, i) => {
              if (picks.includes(i)) {
                c.style.transition = `transform 0.48s cubic-bezier(0.34,1.56,0.64,1) ${picks.indexOf(i) * 0.09}s, box-shadow 0.38s ease`;
                c.style.transform  = 'translateY(-24px) scale(1.1)';
                c.style.boxShadow  = '0 10px 28px rgba(212,175,55,0.5)';
              } else {
                c.style.transition = 'opacity 0.35s ease';
                c.style.opacity    = '0.3';
              }
            });
            setTimeout(resolve, 650); // wait for selection animation to finish
          }, 220);
        }, 120);
      });
    };
  }

  async function fetchReading(stopShuffle) {
    const controller = new AbortController();
    const timeout    = setTimeout(() => controller.abort(), 55000);
    try {
      const url     = `${API}?action=reading`;
      const payload = { ...auth, category: state.category, subcategory: state.subcategory, question: state.question, spread_count: state.spreadCount };
      const res     = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: controller.signal });
      clearTimeout(timeout);
      const data    = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'API Error');
      await stopShuffle(state.spreadCount || 1);
      state.currentReading   = data.reading;
      state.followupPoolIndex = 0;
      state.user.credits     = data.credits_remaining;
      updateCreditsDisplay();
      renderReading(data.reading);
    } catch (err) {
      clearTimeout(timeout);
      show('screen-spread');
      alert(err.name === 'AbortError' ? '占卜逾時，請重試。' : (err.message || '占卜失敗，請重試。'));
    }
  }

  function renderReading(reading) {
    document.getElementById('drawn-cards-row').innerHTML = reading.cards.map(c => `
      <div class="flex flex-col items-center gap-1.5" style="max-width:90px;">
        <div class="relative">
          <img src="${c.img}" class="rounded-lg" style="width:75px; box-shadow:0 2px 10px rgba(180,150,70,0.2);${c.reversed ? 'transform:rotate(180deg);' : ''}">
          ${c.reversed ? '<span class="absolute -top-1 -right-1 text-xs rounded px-1" style="background:#2C1A0E;color:#F5EDD6;font-size:0.6rem;">逆</span>' : ''}
        </div>
        <span class="text-xs text-center font-medium" style="color:#2C1A0E;">${c.name}</span>
        <span class="text-xs text-center leading-tight" style="color:#6B5848;">${c.keywords}</span>
      </div>`).join('');

    document.getElementById('reading-text').textContent = reading.reading_text;
    document.getElementById('followup-thread').innerHTML = '';
    renderFollowupSuggestions();
    show('screen-reading');
  }

  // ─── Follow-up ───────────────────────────────────────────────────────────
  function renderFollowupSuggestions() {
    const pools = FOLLOWUP_POOLS[state.category] || [];
    const pool  = pools[state.followupPoolIndex % pools.length] || [];
    document.getElementById('followup-suggestions').innerHTML = pool.map(q => `
      <button onclick="TarotApp.pickFollowup(this)" data-q="${q.replace(/"/g,'&quot;')}"
        class="w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all"
        style="background:#FFFFFF; border:1px solid rgba(180,150,70,0.25); color:#2C2010;">
        ${q}
      </button>`).join('');
  }

  function refreshSuggestions() {
    state.followupPoolIndex++;
    renderFollowupSuggestions();
  }

  function pickFollowup(btn) {
    document.querySelectorAll('#followup-suggestions button').forEach(b => {
      b.style.borderColor = 'rgba(180,150,70,0.2)';
      b.style.background  = '#FFFFFF';
    });
    btn.style.borderColor = '#D4AF37';
    btn.style.background  = 'rgba(212,175,55,0.08)';
    document.getElementById('followup-input').value = btn.dataset.q;
  }

  async function submitFollowup() {
    const q = document.getElementById('followup-input').value.trim();
    if (!q || !state.currentReading) return;

    const thread  = document.getElementById('followup-thread');
    const qBubble = document.createElement('div');
    qBubble.className = 'flex justify-end';
    qBubble.innerHTML = `<div class="max-w-xs px-4 py-2.5 rounded-2xl text-sm" style="background:#2C1A0E; color:#F5EDD6;">${q}</div>`;
    thread.appendChild(qBubble);

    const typing = document.createElement('div');
    typing.className = 'flex gap-1.5 items-center px-2 py-2';
    typing.innerHTML = '<span class="w-2 h-2 rounded-full bounce-dot" style="background:#2C1A0E;"></span><span class="w-2 h-2 rounded-full bounce-dot" style="background:#2C1A0E;"></span><span class="w-2 h-2 rounded-full bounce-dot" style="background:#2C1A0E;"></span>';
    thread.appendChild(typing);
    try { thread.scrollIntoView({ behavior: 'smooth', block: 'end' }); } catch(e) { thread.scrollIntoView(false); }

    document.getElementById('followup-input').value = '';

    try {
      const data = await api('followup', { reading_id: state.currentReading.id, question: q });
      typing.remove();
      const aBubble = document.createElement('div');
      aBubble.className = 'killa-card p-4';
      aBubble.innerHTML = `<div class="text-xs mb-2 font-semibold" style="color:#D4AF37;">✦ Killa</div><div class="text-sm leading-relaxed whitespace-pre-wrap" style="color:#2C2C2C;">${data.answer}</div>`;
      thread.appendChild(aBubble);
      state.user.credits = data.credits_remaining;
      updateCreditsDisplay();
      renderFollowupSuggestions();
      try { document.getElementById('followup-section').scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch(e) { document.getElementById('followup-section').scrollIntoView(true); }
    } catch (err) {
      typing.remove();
      alert(err.message || '提問失敗，請重試。');
    }
  }

  // ─── History ─────────────────────────────────────────────────────────────
  async function showHistory() {
    show('screen-history');
    const list = document.getElementById('history-list');
    list.innerHTML = '<p class="text-sm text-center" style="color:#8A7A6A;">載入中…</p>';
    try {
      const data2 = await api('history');
      if (!data2.readings.length) {
        list.innerHTML = '<p class="text-sm text-center" style="color:#8A7A6A;">尚無占卜紀錄</p>';
        return;
      }
      list.innerHTML = data2.readings.map(r => `
        <div class="killa-card p-4" style="cursor:pointer;" onclick="TarotApp.showReadingDetail(${r.id})">
          <div class="flex justify-between items-start mb-2">
            <div class="text-xs" style="color:#8A7A6A;">${r.created_at}</div>
            <button onclick="event.stopPropagation();TarotApp.deleteReading(${r.id},this)" style="font-size:0.72rem;color:#8B3A2A;background:rgba(139,58,42,0.08);border:1px solid rgba(139,58,42,0.3);border-radius:8px;padding:0.2rem 0.65rem;cursor:pointer;">刪除</button>
          </div>
          <div class="text-sm font-medium mb-2" style="color:#2C2C2C;">${r.question}</div>
          <div class="flex gap-3 mt-2 mb-3">
            ${r.cards.map(c => `
              <div class="flex flex-col items-center gap-1">
                <img src="${c.img}" style="width:56px;height:auto;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,0.15);${c.reversed ? 'transform:rotate(180deg);' : ''}" title="${c.name}">
                <span style="font-size:0.65rem;color:#8A7A6A;text-align:center;max-width:56px;line-height:1.2;">${c.name}${c.reversed ? '（逆位）' : ''}</span>
              </div>`).join('')}
          </div>
          <p class="text-xs leading-relaxed" style="color:#6B5848;">${r.reading_text.substring(0, 160)}…</p>
          <div class="text-xs mt-2" style="color:#C8A84B;">點擊查看完整解讀 →</div>
        </div>`).join('');
    } catch {
      list.innerHTML = '<p class="text-sm text-center" style="color:#C0392B;">無法載入紀錄</p>';
    }
  }

  async function showReadingDetail(id) {
    show('screen-reading');
    document.getElementById('reading-text').textContent = '載入中…';
    document.getElementById('drawn-cards-row').innerHTML = '';
    document.getElementById('followup-thread').innerHTML = '';
    document.getElementById('followup-section').style.display = 'none';

    try {
      const data = await api('history_detail', { id });
      const r = data.reading;

      state.currentReading = r;

      document.getElementById('drawn-cards-row').innerHTML = r.cards.map(c => `
        <div class="relative flex flex-col items-center gap-1">
          <img src="${c.img}" class="rounded-lg" style="width:75px;box-shadow:0 2px 10px rgba(180,150,70,0.2);${c.reversed ? 'transform:rotate(180deg);' : ''}">
          ${c.reversed ? '<span class="absolute -top-1 -right-1 text-xs rounded px-1" style="background:#2C1A0E;color:#F5EDD6;font-size:0.6rem;">逆</span>' : ''}
        </div>
        <div class="flex flex-col items-center gap-0.5" style="max-width:75px;">
          <span class="text-xs text-center font-medium" style="color:#2C1A0E;">${c.name}</span>
          <span class="text-xs text-center leading-tight" style="color:#6B5848;">${c.keywords}</span>
        </div>`).join('');

      document.getElementById('reading-text').textContent = r.reading_text;

      const thread = document.getElementById('followup-thread');
      data.followups.forEach(f => {
        const qEl = document.createElement('div');
        qEl.className = 'flex justify-end';
        qEl.innerHTML = `<div class="max-w-xs px-4 py-2.5 rounded-2xl text-sm" style="background:#2C1A0E;color:#F5EDD6;">${f.question}</div>`;
        thread.appendChild(qEl);

        const aEl = document.createElement('div');
        aEl.className = 'killa-card p-4';
        aEl.innerHTML = `<div class="text-xs mb-2 font-semibold" style="color:#D4AF37;">✦ Killa</div><div class="text-sm leading-relaxed whitespace-pre-wrap" style="color:#2C2C2C;">${f.answer}</div>`;
        thread.appendChild(aEl);
      });

      document.getElementById('followup-section').style.display = '';
      renderFollowupSuggestions();
    } catch(err) {
      document.getElementById('reading-text').textContent = '載入失敗，請重試。';
    }
  }

  async function deleteReading(id, btn) {
    if (!confirm('確定刪除此占卜紀錄？')) return;
    try {
      await api('history_delete', { id });
      btn.closest('.killa-card').remove();
    } catch (err) { alert(err.message); }
  }

  function newReading() {
    state.currentReading = null;
    state.question       = null;
    show('screen-category');
  }

  document.addEventListener('DOMContentLoaded', init);

  return { goTo, showHistory, showReadingDetail, selectCategory, selectSubcat, pickQuestion, confirmQuestion, selectSpread, confirmSpread, pickFollowup, submitFollowup, refreshSuggestions, deleteReading, newReading };
})();
